import { Heading, Text } from '../../components/index.js'
import * as Dialog from '@radix-ui/react-dialog'
import { useMemo } from 'react'
import type { RegionConfig, IPRedirectModalCopy } from './type.js'
import { buildGeoRedirectRefManual, appendRefParam } from './utils.js'

/** 样式配置 */
export type RegionSelectorModalStyles = {
  /** 弹窗宽度，默认 80% */
  width?: string
  /** 弹窗最大宽度，默认 1000px */
  maxWidth?: string
  /** 弹窗内边距，默认 '30px 32px' */
  padding?: string
  /** 弹窗圆角，默认 16px */
  borderRadius?: string
  /** 标题字号，默认 20px */
  titleFontSize?: string
  /** 分组标题字号，默认 inherit */
  groupTitleFontSize?: string
  /** 卡片高度，默认 56px */
  itemHeight?: string
  /** 卡片背景色，默认 #f5f5f5 */
  itemBgColor?: string
  /** 卡片圆角，默认 8px */
  itemBorderRadius?: string
  /** 卡片间距，默认 8px */
  itemGap?: string
  /** 每行卡片数量，默认 3 */
  columns?: number
}

export type RegionSelectorModalProps = {
  /** 控制弹窗显示/隐藏 */
  open: boolean
  /** 弹窗状态变化回调 */
  onOpenChange: (open: boolean) => void
  /** 地区配置列表 */
  regions: RegionConfig[]
  /** 文案配置 */
  copy: IPRedirectModalCopy
  /** 用户选择地区后的回调 */
  onConfirm?: (region: RegionConfig) => void
  /** 是否自动跳转，默认 false */
  autoRedirect?: boolean
  /** 是否跳转到首页（仅在 autoRedirect 为 true 时生效），默认 false（保留当前路径） */
  redirectToHomepage?: boolean
  /** 样式配置 */
  styles?: RegionSelectorModalStyles
  /** 自定义类名 */
  className?: string
  /**
   * 当前站点的地区代码（用于构建 ref 参数，表示从哪个站点跳转来的）
   * 如果启用 autoRedirect，强烈建议提供此参数以便正确追踪跳转来源
   */
  currentRegionCode?: string
  /**
   * 自定义 ref 参数前缀（用于手动切换站点追踪）
   * @default 'geo_redirect_manual'
   * @example 'user_switch'
   */
  refPrefixManual?: string
}

const defaultStyles: Required<RegionSelectorModalStyles> = {
  width: '80%',
  maxWidth: '1000px',
  padding: '0px',
  borderRadius: '12px',
  titleFontSize: '24px',
  groupTitleFontSize: '18px',
  itemHeight: '54px',
  itemBgColor: '#F5F6F7',
  itemBorderRadius: '0px',
  itemGap: '8px',
  columns: 3,
}

export function RegionSelectorModal({
  open,
  onOpenChange,
  regions,
  copy,
  onConfirm,
  autoRedirect = false,
  redirectToHomepage = false,
  styles: customStyles,
  className,
  currentRegionCode,
  refPrefixManual = 'geo_redirect_manual',
}: RegionSelectorModalProps) {
  const styles = { ...defaultStyles, ...customStyles }

  const handleRegionClick = (region: RegionConfig) => {
    if (onConfirm) {
      onConfirm(region)
    }

    if (autoRedirect) {
      const currentSearch = window.location.search

      if (redirectToHomepage) {
        // 使用当前站点代码构建 ref 参数（表示从哪个站点跳转来的）
        const refValue = currentRegionCode
          ? buildGeoRedirectRefManual(currentRegionCode, refPrefixManual)
          : buildGeoRedirectRefManual(region.code, refPrefixManual) // 向后兼容：如果没有提供 currentRegionCode，使用目标站点代码
        const targetUrl = appendRefParam(region.url, refValue)
        window.location.href = targetUrl
      } else {
        const currentPath = window.location.pathname
        const currentHash = window.location.hash
        const targetBaseUrl = region.url.replace(/\/$/, '')
        const baseUrl = `${targetBaseUrl}${currentPath}${currentSearch}`
        // 使用当前站点代码构建 ref 参数（表示从哪个站点跳转来的）
        const refValue = currentRegionCode
          ? buildGeoRedirectRefManual(currentRegionCode, refPrefixManual)
          : buildGeoRedirectRefManual(region.code, refPrefixManual) // 向后兼容：如果没有提供 currentRegionCode，使用目标站点代码
        const fullTargetUrl = appendRefParam(baseUrl, refValue) + currentHash
        window.location.href = fullTargetUrl
      }
    }

    onOpenChange(false)
  }

  const itemWidth = `calc((100% - ${styles.itemGap} * ${styles.columns - 1}) / ${styles.columns})`

  const groupedRegions = useMemo(() => {
    const groups: Record<string, RegionConfig[]> = {}
    regions.forEach(region => {
      const group = region.group || 'Other'
      if (!groups[group]) {
        groups[group] = []
      }
      groups[group].push(region)
    })
    return groups
  }, [regions])

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <style>{`
          .region-item {
            width: 100%;
          }
          @media (min-width: 1024px) {
            .region-item {
              width: ${itemWidth};
            }
          }
          .modal-content {
            padding: 0 16px 32px 16px;
            overflow-y: auto;
            max-height: calc(90vh - 92px);
            max-height: calc(90dvh - 92px);
            -webkit-overflow-scrolling: touch;
          }
          @media (min-width: 1024px) {
            .modal-content {
              padding: 0 32px 32px 32px;
              max-height: none;
            }
          }
          .modal-dialog {
            width: 100% !important;
            max-width: 100% !important;
            top: 100px !important;
            bottom: 0 !important;
            height: calc(100vh - 100px) !important;
            max-height: none !important;
          }
          @media (min-width: 1024px) {
            .modal-dialog {
              width: ${styles.width} !important;
              max-width: ${styles.maxWidth} !important;
              top: 50% !important;
              bottom: auto !important;
              transform: translate(-50%, -50%) !important;
              height: auto !important;
              max-height: calc(100vh - 32px) !important;
            }
          }
          .modal-title {
            text-align: center;
          }
          @media (max-width: 1024px) {
            .modal-title {
              text-align: left;
              font-size: 20px !important;
            }
          }
          .region-group {
            gap: 12px;
          }
          @media (max-width: 1024px) {
            .region-group {
              gap: 0;
            }
          }
          .group-title {
            margin-bottom: 8px;
          }
          @media (max-width: 1024px) {
            .group-title {
              font-size: 16px !important;
            }
          }
        `}</style>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[9998] bg-black/50" />
        <Dialog.Content
          className={`modal-dialog data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed left-1/2 z-[9999] -translate-x-1/2 overflow-hidden bg-white shadow-lg duration-200 ${className || ''}`}
          style={{
            padding: styles.padding,
            borderRadius: styles.borderRadius,
          }}
        >
          <div className="flex flex-col">
            <div className="relative flex items-center justify-end p-[16px]">
              <Dialog.Close asChild>
                <button
                  className="inline-flex size-[28px] items-center justify-center rounded-full hover:bg-[#f5f5f5] focus:outline-none"
                  style={{ color: '#767880' }}
                  aria-label="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </Dialog.Close>
            </div>

            <div className="modal-content flex flex-col gap-[24px]">
              <Dialog.Title asChild>
                <Heading
                  as="h2"
                  className="modal-title text-[#1F2021]"
                  style={{ fontSize: styles.titleFontSize, fontWeight: '700' }}
                >
                  {copy.title}
                </Heading>
              </Dialog.Title>
              {Object.entries(groupedRegions).map(([groupName, groupRegions]) => (
                <div key={groupName} className="region-group flex flex-col">
                  <Text
                    size={3 as any}
                    className="group-title text-left font-semibold text-[#1F2021]"
                    style={{ fontSize: styles.groupTitleFontSize, fontWeight: '700' }}
                  >
                    {groupName}
                  </Text>
                  <div className="flex flex-wrap justify-start" style={{ gap: styles.itemGap }}>
                    {groupRegions.map(region => (
                      <button
                        key={region.code}
                        onClick={() => handleRegionClick(region)}
                        className="region-item flex items-center gap-[12px] px-[16px] transition-colors hover:opacity-80"
                        style={{
                          height: styles.itemHeight,
                          backgroundColor: styles.itemBgColor,
                          borderRadius: styles.itemBorderRadius,
                        }}
                      >
                        <Text
                          size={2 as any}
                          className="flex-1 text-left font-medium"
                          style={{ color: '#000', fontWeight: '700' }}
                        >
                          {region.name}
                        </Text>
                        {(region.language || region.currency) && (
                          <Text size={2 as any} className="shrink-0 text-[16px]" style={{ color: '#767880' }}>
                            {region.language}
                            {region.language && region.currency && ' / '}
                            {region.currency}
                          </Text>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
